import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, ArrowRight, Clock, Users, FileCheck, BarChart3, CircleDot, XCircle, MinusCircle, PlusCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { seoContent, content } from '../data/caseStudyInstructionSystemData';

const CaseStudyInstructionSystem = () => {
  const { language: lang } = useLanguage();

  const seo = seoContent[lang] || seoContent.de;
  const t = content[lang];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E7F0FA] to-white pt-20">
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path="/case-study/instruction-system"
        lang={lang}
      />
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-[#C8D8E8]">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link 
            to="/portfolio" 
            className="inline-flex items-center gap-2 text-[#2E5E99] hover:text-[#1E4F85] transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.backLink}
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <span className="px-3 py-1 bg-[#2E5E99] text-white text-sm font-medium rounded-full">
              {t.title}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#0D2440]">
            {t.subtitle}
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Hero Image */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="/assets/instruction-system.jpeg" 
              alt={t.subtitle}
              className="w-full h-[400px] object-cover object-[75%_30%]"
            />
          </div>
        </motion.section>

        {/* Ausgangssituation */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="bg-white rounded-xl p-6 md:p-8 border border-[#C8D8E8] shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.caseStudy.situation.title}</h3>
            </div>
            <p className="text-[#5A7A9B] mb-4">{t.caseStudy.situation.text}</p>
            <ul className="space-y-2 mb-4">
              {t.caseStudy.situation.points.map((point) => (
                <li key={point} className="flex items-start gap-2 text-[#5A7A9B]">
                  <CircleDot className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
            <p className="text-[#5A7A9B] italic">{t.caseStudy.situation.conclusion}</p>
          </div>
        </motion.section>

        {/* Herausforderung */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div className="bg-white rounded-xl p-6 md:p-8 border border-[#C8D8E8] shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.caseStudy.challenge.title}</h3>
            </div>
            <p className="text-[#5A7A9B] mb-4">{t.caseStudy.challenge.text}</p>
            <ul className="space-y-2">
              {t.caseStudy.challenge.points.map((point) => (
                <li key={point} className="flex items-start gap-2 text-[#5A7A9B]">
                  <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </motion.section>

        {/* Lösung */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <div className="bg-gradient-to-br from-[#E7F0FA] to-white rounded-xl p-6 md:p-8 border border-[#2E5E99]/30 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#2E5E99]/10 rounded-lg flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-[#2E5E99]" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.caseStudy.solution.title}</h3>
            </div>
            <p className="text-[#5A7A9B] mb-4">{t.caseStudy.solution.text}</p>
            <p className="text-[#0D2440] font-medium mb-3">{t.caseStudy.solution.subtitle}</p>
            <ul className="space-y-2 mb-4">
              {t.caseStudy.solution.points.map((point) => (
                <li key={point} className="flex items-start gap-2 text-[#5A7A9B]">
                  <CheckCircle className="w-5 h-5 text-[#2E5E99] mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
            <p className="text-[#2E5E99] font-medium">{t.caseStudy.solution.conclusion}</p>
          </div>
        </motion.section>

        {/* Ergebnis */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-12"
        >
          <div className="bg-white rounded-xl p-6 md:p-8 border border-[#C8D8E8] shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.caseStudy.result.title}</h3>
            </div>
            <p className="text-[#5A7A9B] mb-4">{t.caseStudy.result.text}</p>
            <ul className="space-y-2">
              {t.caseStudy.result.points.map((point) => (
                <li key={point} className="flex items-start gap-2 text-[#5A7A9B]">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </motion.section>

        {/* Vorher/Nachher Vergleich */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-[#0D2440] text-center mb-8">{t.caseStudy.comparison.title}</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Vorher */}
            <div className="bg-red-50 rounded-xl p-6 border border-red-200">
              <h4 className="text-xl font-semibold text-red-700 mb-4">{t.caseStudy.comparison.before.title}</h4>
              <ul className="space-y-3">
                {t.caseStudy.comparison.before.points.map((point) => (
                  <li key={point} className="flex items-center gap-2 text-[#5A7A9B]">
                    <MinusCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Nachher */}
            <div className="bg-green-50 rounded-xl p-6 border border-green-200">
              <h4 className="text-xl font-semibold text-green-700 mb-4">{t.caseStudy.comparison.after.title}</h4>
              <ul className="space-y-3">
                {t.caseStudy.comparison.after.points.map((point) => (
                  <li key={point} className="flex items-center gap-2 text-[#5A7A9B]">
                    <PlusCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="bg-gradient-to-r from-[#2E5E99] to-[#1E4F85] rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              {t.cta.title}
            </h2>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto">
              {t.cta.text}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking">
                <Button className="bg-white text-[#2E5E99] hover:bg-white/90 px-8 py-3 h-auto text-base">
                  {t.cta.button}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/instruction-system">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-3 h-auto text-base">
                  {t.cta.systemLink}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default CaseStudyInstructionSystem;
